import CreateClassroomDialog from '@/components/create-classroom-dialog';
import { DataTable } from '@/components/data-table';
import LanguageBox from '@/components/language-box';
import ContentLayout from '@/components/layout/ContentLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/contexts/AuthContext';
import { deleteClass, editClass, getAllStudents, getClassStudents, registerClass, registerStudentBatchToClass, updateClassStudents, useGetAllClassInfo, useGetAllStudents, useGetClassStudents } from '@/services/generatedAPIs';
import { ClassroomEditRequest, ClassroomInfoResponse, EltResponseClassroomGetAllStudentsResponse, EltResponseClassroomGetStudentsResponse, Student, StudentCreateRequest } from '@/services/generatedAPIs.schemas';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuLabel, DropdownMenuItem, DropdownMenuSeparator } from '@radix-ui/react-dropdown-menu';
import { ColumnDef } from '@tanstack/react-table';
import { ArrowLeftRight, ArrowUpDown } from 'lucide-react';
import React, { use, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { mutate, useSWRConfig } from 'swr';
import { ScrollArea } from '@/components/ui/scroll-area';
import XLSX from 'xlsx';
import { useAsyncAction } from '@/hooks/use-async-action';
import ImportFormatImgUrl from '@/assets/images/import-format.png';
import ImportClassImgUrl from '@/assets/images/import-classes.png';
import { generateSalt, hashPassword } from '@/lib/crypto';
import { isEN } from '@/utils/i18n';

type TableData = {
  classroomInfoResponse: ClassroomInfoResponse,
  name: string,
}

type ClassData = {
  classname?: string,
  name: string,
  enname: string,
  email: string,
  password: string,
  sex: string,
  grade: string | number,
  classno?: string,
  schoolno?: string,
}

const AdminPage = () => {
  const { t, i18n } = useTranslation();
  const { getCurrentUser } = useAuth();
  const { data, error, isLoading } = useGetAllClassInfo({
    schoolName: getCurrentUser()!.schoolName,
  }, { swr: { swrKey: "AdminPage-useGetAllClassInfo" } });
  const [classData, setClassData] = useState<ClassData[]>([])
  const { isLoading: isBatchImporting, execute: executeBatchImport } = useAsyncAction({ showErrorToast: false });

  const columns: ColumnDef<TableData>[] = [
    {
      id: "select",
      header: ({ table }) => (
        //   <Checkbox
        //     checked={
        //       table.getIsAllPageRowsSelected() ||
        //       (table.getIsSomePageRowsSelected() && "indeterminate")
        //     }
        //     onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        //     aria-label="Select all"
        //   />
        <></>
      ),
      cell: ({ row }) => (
        //   <Checkbox
        //     checked={row.getIsSelected()}
        //     onCheckedChange={(value) => row.toggleSelected(!!value)}
        //     aria-label="Select row"
        //   />
        <></>
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "name",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("component.classname")}
            <ArrowUpDown />
          </Button>
        )
      },
      cell: ({ row }) => (
        <div>{row.original.name}</div>
      ),
    },
    {
      accessorKey: "studentCount",
      header: () => { return (<span>{t("component.studentcount")}</span>) },
      cell: ({ row }) => <div>{row.original.classroomInfoResponse.studentCount}</div>,
    },
    // {
    //   accessorKey: "amount",
    //   header: () => <div className="text-right">Amount</div>,
    //   cell: ({ row }) => {
    //     const amount = parseFloat(row.getValue("amount"))

    //     // Format the amount as a dollar amount
    //     const formatted = new Intl.NumberFormat("en-US", {
    //       style: "currency",
    //       currency: "USD",
    //     }).format(amount)

    //     return <div className="text-right font-medium">{formatted}</div>
    //   },
    // },
    {
      id: "actions",
      enableHiding: false,
      cell: ({ row }) => {
        const info = row.original.classroomInfoResponse;
        const { mutate } = useSWRConfig()
        const { getCurrentUser } = useAuth();

        const [allStudents, setAllStudents] = React.useState<Student[]>([]);
        const [classStudents, setClassStudents] = React.useState<Student[]>([]);

        const [allData, setAllData] = useState<EltResponseClassroomGetAllStudentsResponse>();
        const [classData, setClassData] = useState<EltResponseClassroomGetStudentsResponse>();

        useEffect(() => {
          getAllStudents({ schoolName: getCurrentUser()!.schoolName }).then((rsp) => {
            if (rsp.code === 200 && rsp.data) {
              setAllData(rsp);
            }
          });

          getClassStudents({ classroomInfoId: info.id }).then((rsp) => {
            if (rsp.code === 200 && rsp.data) {
              setClassData(rsp);
            }
          });
        }, []);

        useEffect(() => {
          if (allData && allData.code === 200 && classData && classData.code === 200) {
            let allStudents = allData.data!.students;
            let classStudents = classData.data!.students;
            let classEmails = classStudents.map((student) => student.email);
            allStudents = allStudents.filter((student) => !classEmails.includes(student.email!));

            allStudents.sort((a, b) => a.name.localeCompare(b.name));
            classStudents.sort((a, b) => a.name.localeCompare(b.name));
            setAllStudents(allStudents);
            setClassStudents(classStudents);
          }
        }, [allData, classData]);

        const [data, setData] = React.useState<ClassData[]>([]);
        const [uploading, setIsUploading] = React.useState(false);
        const handleFile = (files: any) => {
          if (!files || files.length === 0) {
            return;
          }

          const file = files[0];

          setIsUploading(true);

          // 创建文件读取器
          const reader = new FileReader();

          // 读取完成回调
          reader.onload = (e) => {
            try {
              // 1. 获取二进制数据
              const binaryData = new Uint8Array(e.target.result);

              // 2. 解析Excel文件
              const workbook = XLSX.read(binaryData, { type: 'array' });

              // 3. 获取第一个工作表
              const worksheet = workbook.Sheets[workbook.SheetNames[0]];

              // 4. 转换为JSON数据
              const jsonData: ClassData[] = XLSX.utils.sheet_to_json(worksheet);

              let studentData = jsonData.filter((value) => { return value.email.indexOf('注意') === -1; });

              for (let i = 0; i < studentData.length; ++i) {
                const value = studentData[i];
                if (!value.name || value.name.length >= 100) {
                  alert(t("component.namealert"));
                  return;
                }

                if (value.enname && value.enname.length >= 100) {
                  alert(t("component.namealert"));
                  return;
                }

                const validSexSet = new Set(['男', '女', 'male', 'female', 'M', 'F', 'X', 'x']);
                if (!value.sex || !validSexSet.has(value.sex)) {
                  alert(t("component.sexalert"));
                  return;
                } else {
                  if (value.sex === '男' || value.sex === 'male' || value.sex === 'M') {
                    value.sex = 'male';
                  } else if (value.sex === '女' || value.sex === 'female' || value.sex === 'F') {
                    value.sex = 'female';
                  } else if (value.sex === 'X' || value.sex === 'x') {
                    value.sex = 'X';
                  }
                }

                switch (value.grade) {
                  case 'Primary 1': value.grade = 1; break;
                  case 'Primary 2': value.grade = 2; break;
                  case 'Primary 3': value.grade = 3; break;
                  case 'Primary 4': value.grade = 4; break;
                  case 'Primary 5': value.grade = 5; break;
                  case 'Primary 6': value.grade = 6; break;
                  case 'Secondary 1': value.grade = 7; break;
                  case 'Secondary 2': value.grade = 8; break;
                  case 'Secondary 3': value.grade = 9; break;
                  case 'Secondary 4': value.grade = 10; break;
                  case 'Secondary 5': value.grade = 11; break;
                  case 'Secondary 6': value.grade = 12; break;
                  default: {
                    alert(t("component.classnoalert"));
                    return;
                  }
                }

                if (value.classno && !Number.isInteger(Number(value.classno))) {
                  alert(t("component.classnoalert"));
                  return;
                }

                if (value.schoolno && !Number.isInteger(Number(value.schoolno))) {
                  alert(t("component.schoolnoalert"));
                  return;
                }

                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(value.email)) {
                  alert(t("component.emailalert"));
                  return;
                }

                const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
                if (!passwordRegex.test(value.password)) {
                  alert(t("component.passwordalert"));
                  return;
                }
              }

              // 5. 分离表头和数据行
              setData(studentData);
            } catch (error) {
              console.error('解析错误:', error);
              alert(t("component.fileerror"));
            } finally {
              setIsUploading(false);
            }
          };

          reader.onerror = (e) => {
            console.log('reader error', e);
            setIsUploading(false);
          }

          // 以ArrayBuffer格式读取文件
          reader.readAsArrayBuffer(file);
        };

        const { isLoading, execute } = useAsyncAction({ showErrorToast: false });
        const [classInfo, setClassInfo] = useState<ClassroomEditRequest>(info);

        return (
          <div className='flex place-content-end gap-5'>
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="outline">{t("teacher.editclass")}</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogTitle>{t("teacher.classinfo")}</DialogTitle>
                <div className="grid gap-4 py-4">
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="name" className="text-right">
                      {t("component.classname")}
                    </Label>
                    <Input name="name" defaultValue={info.name} onChange={evt => setClassInfo({ id: classInfo.id, name: evt.target.value })} className="col-span-3" />
                  </div>
                  <DialogClose asChild>
                    <div className='flex place-content-evenly'>
                      <Button variant="outline">{t("component.cancel")}</Button>
                      <Button type="submit" variant="default" className='bg-primary' loading={isLoading} onClick={() => {
                        if (!classInfo.id || !classInfo.name) {
                          return;
                        }
                        execute(async () => {
                          const res = await editClass(classInfo);
                          if (res.code === 200) {
                            mutate("AdminPage-useGetAllClassInfo");
                            console.log("Create classroom successful", res.data);
                          } else {
                            console.error("Create classroom failed", res.code, res.message);
                          }
                        });
                      }}>{t("component.done")}</Button>
                    </div>
                  </DialogClose>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog>
              <DialogTrigger asChild>
                <Button variant="outline">{t("teacher.addstudent")}</Button>
              </DialogTrigger>
              <DialogContent className="md:max-w-2xl lg:max-w-4xl">
                <DialogHeader>
                  <DialogTitle>{t("component.addstudenttoclass")}</DialogTitle>
                  <DialogDescription>{t("component.classname")}: {info.name}</DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-2">
                  <div className='flex flex-row gap-3 items-stretch'>
                    <div className="flex-1 flex flex-col rounded-lg border bg-muted">
                      <div className="px-4 py-2 border-b bg-muted/50 rounded-t-lg">
                        <h4 className="text-sm font-medium">{t("component.alreadyadded")} ({classStudents.length})</h4>
                      </div>
                      <ScrollArea className="h-72">
                        <div className="p-2">
                          <table className="table-fixed w-full">
                            <thead>
                              <tr className="text-xs text-muted-foreground">
                                <th className="py-1.5 font-medium">{t("user.name")}</th>
                                <th className="py-1.5 font-medium">{t("user.sex")}</th>
                                <th className="py-1.5 font-medium">{t("user.email")}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {
                                classStudents.map((student) => {
                                  return (
                                    <tr key={student.email} className="cursor-pointer hover:bg-red-50 transition-colors rounded" onClick={() => {
                                      classStudents.splice(classStudents.indexOf(student), 1);
                                      allStudents.push(student);
                                      allStudents.sort((a, b) => a.name.localeCompare(b.name));
                                      setClassStudents([...classStudents]);
                                      setAllStudents([...allStudents]);
                                    }}>
                                      <td className='text-center text-sm py-1.5'>{isEN(i18n) ? (student.nameEn || student.name) : student.name}</td>
                                      <td className='text-center text-sm py-1.5'>{student.sex}</td>
                                      <td className='text-center text-sm py-1.5'>{student.email}</td>
                                    </tr>
                                  )
                                })
                              }
                            </tbody>
                          </table>
                        </div>
                      </ScrollArea>
                      <p className='text-slate-500 text-xs italic ml-2'>*{t("component.clicktoremove")}</p>
                    </div>
                    <div className="flex items-center"><ArrowLeftRight className="h-5 w-5 text-muted-foreground" /></div>
                    <div className="flex-1 flex flex-col rounded-lg border bg-muted">
                      <div className="px-4 py-2 border-b bg-muted/50 rounded-t-lg">
                        <h4 className="text-sm font-medium">{t("component.availablestudent")} ({allStudents.length})</h4>
                      </div>
                      <ScrollArea className="h-72">
                        <div className="p-2">
                          <table className="table-fixed w-full">
                            <thead>
                              <tr className="text-sm text-muted-foreground">
                                <th className="py-1.5 font-semibold">{t("user.name")}</th>
                                <th className="py-1.5 font-semibold">{t("user.sex")}</th>
                                <th className="py-1.5 font-semibold">{t("user.email")}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {
                                allStudents.map((student) => {
                                  return (
                                    <tr key={student.email} className="cursor-pointer hover:bg-green-50 transition-colors rounded" onClick={() => {
                                      allStudents.splice(allStudents.indexOf(student), 1);
                                      classStudents.push(student);
                                      classStudents.sort((a, b) => a.name.localeCompare(b.name));
                                      setClassStudents([...classStudents]);
                                      setAllStudents([...allStudents]);
                                    }}>
                                      <td className='text-center text-sm py-1.5'>{isEN(i18n) ? (student.nameEn || student.name) : student.name}</td>
                                      <td className='text-center text-sm py-1.5'>{student.sex}</td>
                                      <td className='text-center text-sm py-1.5'>{student.email}</td>
                                    </tr>
                                  )
                                })
                              }
                            </tbody>
                          </table>
                        </div>
                      </ScrollArea>
                      <p className='text-slate-500 text-xs italic ml-2'>*{t("component.clicktoadd")}</p>
                    </div>
                  </div>
                  <DialogFooter className="gap-2">
                    <DialogClose asChild>
                      <Button variant="outline">{t("component.cancel")}</Button>
                    </DialogClose>
                    <DialogClose asChild>
                      <Button variant="default" loading={isLoading} onClick={() => {
                        execute(async () => {
                          const res = await updateClassStudents({
                            classroomInfoId: info.id,
                            studentIds: classStudents.map((student) => student.id!)
                          });
                          if (res.code === 200) {
                            mutate("AdminPage-useGetAllClassInfo");
                            console.log("Create classroom successful", res.data);
                          } else {
                            console.error("Create classroom failed", res.code, res.message);
                          }
                        });
                      }}>{t("component.done")}</Button>
                    </DialogClose>
                  </DialogFooter>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog>
              <DialogTrigger asChild>
                <Button variant="outline">{t("component.batchimport")}</Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-240">
                <DialogTitle>{t("component.importtips")}</DialogTitle>
                <img src={ImportFormatImgUrl}></img>
                <div className='flex flex-col gap-2'>
                  <Input id="file" type="file" accept=".xlsx, .xls, .csv" onChange={(e) => handleFile(e.target.files)} className='file:bg-primary file:text-accent file:cursor-pointer file:mr-2 bg-primary text-accent cursor-pointer' />
                  {
                    data?.length > 0 &&
                    <ScrollArea className="h-60 w-full rounded-md border">
                      <table className='table-fixed w-full'>
                        <thead><tr>
                          <th>{t("user.email")}</th>
                          <th>{t("user.password")}</th>
                          <th>{t("user.name")}</th>
                          <th>{t("user.enname")}</th>
                          <th>{t("user.sex")}</th>
                          <th>{t("common.grade")}</th>
                          <th>{t("common.classID")}</th>
                          <th>{t("common.schoolID")}</th>
                          </tr></thead>
                        <tbody>
                          {data.map((row, index) => (
                            <tr key={index}>
                              <td className='text-center'>{row.email}</td>
                              <td className='text-center'>{row.password}</td>
                              <td className='text-center'>{row.name}</td>
                              <td className='text-center'>{row.enname}</td>
                              <td className='text-center'>{row.sex}</td>
                              <td className='text-center'>{row.grade}</td>
                              <td className='text-center'>{row.classno}</td>
                              <td className='text-center'>{row.schoolno}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </ScrollArea>
                  }
                </div>
                <DialogClose asChild>
                  {data?.length > 0 &&
                    <div className='flex place-content-evenly'>
                      <Button type="submit" variant="outline" onClick={() => {
                        setData([]);
                      }}>{t("component.cancel")}</Button>
                      <Button type="submit" variant="default" className='bg-primary' loading={isLoading} onClick={() => {
                        const studentCreateRequests: StudentCreateRequest[] = [];
                        data.forEach((student) => {
                          const salt = generateSalt();
                          studentCreateRequests.push({
                              name: student.name,
                              nameEn: student.enname,
                              schoolName: getCurrentUser()!.schoolName,
                              email: student.email,
                              password: hashPassword(student.password + salt),
                              gradeLevel: student.grade as number,
                              classNumber: student.classno ? Number(student.classno) : undefined,
                              schoolNumber: student.schoolno ? Number(student.schoolno) : undefined,
                              salt: salt,
                              sex: student.sex,
                              captcha: '',
                              strn: '',
                          })
                        });
                        execute(async () => {
                          const res = await registerStudentBatchToClass({
                            studentInfos: studentCreateRequests,
                            classroomInfoIds: (new Array(data.length)).fill(info.id),
                          });
                          if (res.code === 200) {
                            setData([]);
                            mutate("AdminPage-useGetAllClassInfo");
                            console.log("Create classroom successful", res.data);
                          } else {
                            console.error("Create classroom failed", res.code, res.message);
                          }
                        });
                      }}>{t("component.done")}</Button>
                    </div>
                  }
                </DialogClose>
              </DialogContent>
            </Dialog>

            {/* <Dialog>
              <DialogTrigger asChild>
                <Button variant="destructive">{t("teacher.delete")}</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t("component.deleteClassConfirmTitle")}</DialogTitle>
                  <DialogDescription>{t("component.deleteClassConfirmDesc")}</DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <DialogClose asChild>
                    <Button variant="outline">{t("component.cancel")}</Button>
                  </DialogClose>
                  <DialogClose asChild>
                    <Button variant="destructive" onClick={() => {
                      setIsLoading(true);
                      deleteClass({ id: info.id }).then((res) => {
                        if (res.code === 200) {
                          mutate("AdminPage-useGetAllClassInfo");
                        } else {
                          console.error("Delete classroom failed", res.code, res.message);
                        }
                      }).catch((error) => {
                        console.error("Delete classroom error", error);
                      }).finally(() => {
                        setIsLoading(false);
                      });
                    }}>{t("teacher.delete")}</Button>
                  </DialogClose>
                </DialogFooter>
              </DialogContent>
            </Dialog> */}
          </div>
        )
      },
    },
  ]

  const handleClassFile = (files: any) => {
    if (!files || files.length === 0) {
      return;
    }

    const file = files[0];

    // 创建文件读取器
    const reader = new FileReader();

    // 读取完成回调
    reader.onload = (e) => {
      try {
        // 1. 获取二进制数据
        const binaryData = new Uint8Array(e.target.result);

        // 2. 解析Excel文件
        const workbook = XLSX.read(binaryData, { type: 'array' });

        // 3. 获取第一个工作表
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];

        // 4. 转换为JSON数据
        const jsonData: ClassData[] = XLSX.utils.sheet_to_json(worksheet);

        let classData = jsonData.filter((value) => { return value.email.indexOf('注意') === -1; });

        for (let i = 0; i < classData.length; ++i) {
          const value = classData[i];
          if (!value.name || value.name.length >= 100) {
            alert(t("component.namealert"));
            return;
          }

          if (value.enname && value.enname.length >= 100) {
            alert(t("component.namealert"));
            return;
          }

          const validSexSet = new Set(['男', '女', 'male', 'female', 'M', 'F', 'X', 'x']);
          if (!value.sex || !validSexSet.has(value.sex)) {
            alert(t("component.sexalert"));
            return;
          } else {
            if (value.sex === '男' || value.sex === 'male' || value.sex === 'M') {
              value.sex = 'male';
            } else if (value.sex === '女' || value.sex === 'female' || value.sex === 'F') {
              value.sex = 'female';
            } else if (value.sex === 'X' || value.sex === 'x') {
              value.sex = 'X';
            }
          }

          switch (value.grade) {
            case 'Primary 1': value.grade = 1; break;
            case 'Primary 2': value.grade = 2; break;
            case 'Primary 3': value.grade = 3; break;
            case 'Primary 4': value.grade = 4; break;
            case 'Primary 5': value.grade = 5; break;
            case 'Primary 6': value.grade = 6; break;
            case 'Secondary 1': value.grade = 7; break;
            case 'Secondary 2': value.grade = 8; break;
            case 'Secondary 3': value.grade = 9; break;
            case 'Secondary 4': value.grade = 10; break;
            case 'Secondary 5': value.grade = 11; break;
            case 'Secondary 6': value.grade = 12; break;
            default: value.grade = 7;
          }

          if (value.classno && !Number.isInteger(Number(value.classno))) {
            alert(t("component.classnoalert"));
            return;
          }

          if (value.schoolno && !Number.isInteger(Number(value.schoolno))) {
            alert(t("component.schoolnoalert"));
            return;
          }

          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(value.email)) {
            alert(t("component.emailalert"));
            return;
          }

          const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
          if (!passwordRegex.test(value.password)) {
            alert(t("component.passwordalert"));
            return;
          }
        }

        setClassData(classData);
      } catch (error) {
        console.error('解析错误:', error);
        alert(t("component.fileerror"));
      }
    };

    reader.onerror = (e) => {
      console.log('reader error', e);
    }

    // 以ArrayBuffer格式读取文件
    reader.readAsArrayBuffer(file);
  };

  return (
    <ContentLayout title={t('teacher.admin')}>
      <div className='p-5 bg-muted m-2 rounded-2xl'>
        {
          data?.data?.classroomInfoResponseList &&
          <DataTable data={data.data.classroomInfoResponseList.map((rsp) => {
            return { name: rsp.name, classroomInfoResponse: rsp }
          })} columns={columns} filterColumn='name' filterName={t('component.classname')} renderButton={() => {
            return (
              <div className='flex flex-row gap-4'>
                <CreateClassroomDialog />

                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="outline">{t("component.importclass")}</Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-240">
                    <DialogTitle>{t("component.importtips")}</DialogTitle>
                    <img src={ImportClassImgUrl}></img>
                    <div className='flex flex-col gap-2'>
                      <Input id="file" type="file" accept=".xlsx, .xls, .csv" onChange={(e) => handleClassFile(e.target.files)} className='bg-blue-500' />
                      {
                        classData?.length > 0 &&
                        <ScrollArea className="h-60 w-full rounded-md border">
                          <table className='table-fixed w-full'>
                            <thead><tr>
                              <th>{t("user.email")}</th>
                              <th>{t("user.password")}</th>
                              <th>{t("user.name")}</th>
                              <th>{t("user.enname")}</th>
                              <th>{t("user.sex")}</th>
                              <th>{t("common.grade")}</th>
                              <th>{t("common.class")}</th>
                              <th>{t("common.classID")}</th>
                              <th>{t("common.schoolID")}</th>
                              </tr></thead>
                            <tbody>
                              {classData.map((row, index) => (
                                <tr key={index}>
                                  <td className='text-center'>{row.email}</td>
                                  <td className='text-center'>{row.password}</td>
                                  <td className='text-center'>{row.name}</td>
                                  <td className='text-center'>{row.enname}</td>
                                  <td className='text-center'>{row.sex}</td>
                                  <td className='text-center'>{row.grade}</td>
                                  <td className='text-center'>{row.classname}</td>
                                  <td className='text-center'>{row.classno}</td>
                                  <td className='text-center'>{row.schoolno}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </ScrollArea>
                      }
                    </div>
                    <DialogClose asChild>
                      {classData?.length > 0 &&
                        <div className='flex place-content-evenly'>
                          <Button type="submit" variant="outline" onClick={() => {
                            setClassData([]);
                          }}>{t("component.cancel")}</Button>
                          <Button type="submit" variant="default" className='bg-primary' loading={isBatchImporting} onClick={() => {
                            const classStudentMap = new Map<string, StudentCreateRequest[]>();
                            const schoolName = getCurrentUser()!.schoolName;
                            for (let data of classData) {
                              const studentCreateRequest: StudentCreateRequest = {
                                name: data.name,
                                nameEn: data.enname,
                                schoolName: schoolName,
                                email: data.email,
                                password: data.password,
                                gradeLevel: data.grade as number,
                                classNumber: data.classno ? Number(data.classno) : undefined,
                                schoolNumber: data.schoolno ? Number(data.schoolno): undefined,
                                salt: '',
                                sex: data.sex,
                                captcha: '',
                                strn: '',
                              };
                              if (classStudentMap.has(data.classname!)) {
                                classStudentMap.get(data.classname!)!.push(studentCreateRequest);
                              } else {
                                classStudentMap.set(data.classname!, [studentCreateRequest])
                              }
                            }

                            executeBatchImport(async () => {
                              const promises = Array.from(classStudentMap.entries()).map(async ([classname, students]) => {
                                const res = await registerClass({
                                  name: classname,
                                  schoolName,
                                  grade: students[0].gradeLevel,
                                  override: true,
                                });
                                if (res.code === 200 && res.data) {
                                  console.log("Create classroom successful", res.data);
                                  students.forEach((student) => {
                                    const salt = generateSalt();
                                    student.schoolName = schoolName;
                                    student.password = hashPassword(student.password + salt);
                                    student.salt = salt;
                                  });
                                  const batchRes = await registerStudentBatchToClass({
                                    studentInfos: students,
                                    classroomInfoIds: (new Array(students.length)).fill(res.data.id),
                                  });
                                  if (batchRes.code === 200) {
                                    console.log("Create classroom successful", batchRes.data);
                                  } else {
                                    console.error("Create classroom failed", batchRes.code, batchRes.message);
                                  }
                                } else {
                                  console.error("Create classroom failed", res.code, res.message);
                                }
                              });
                              await Promise.all(promises);
                              mutate("AdminPage-useGetAllClassInfo");
                            });
                          }}>{t("component.done")}</Button>
                        </div>
                      }
                    </DialogClose>
                  </DialogContent>
                </Dialog>
              </div>
            )
          }} />
        }
      </div>
    </ContentLayout>
  );
};

export default AdminPage;